import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { DataTable } from "@/components/shared/DataTable";
import { FormDialog } from "@/components/shared/FormDialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Badge } from "@/components/ui/badge";
import { SelectValue, SelectTrigger, SelectContent, SelectItem, Select } from "@/components/ui/select";
import { RemixIcon } from "@/components/shared/RemixIcon";

const userSchema = z.object({
  email: z.string().email("Valid email is required"),
  role: z.enum(["admin", "manager", "customer", "tailor", "factory_manager"]),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().optional(),
  phone: z.string().optional(),
  address: z.string().optional(),
  active: z.boolean().default(true)
});
type UserFormValues = z.infer<typeof userSchema>;

const Customers = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const {
    toast
  } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const form = useForm<UserFormValues>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      email: "",
      role: "customer",
      firstName: "",
      lastName: "",
      phone: "",
      address: "",
      active: true
    }
  });
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const {
        data,
        error
      } = await supabase.from("users").select("*").order("created_at", {
        ascending: false
      });
      if (error) throw error;
      setUsers(data || []);
    } catch (error: any) {
      toast({
        title: "Error fetching users",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchUsers();
  }, []);
  const onSubmit = async (values: UserFormValues) => {
    try {
      const profile = {
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        address: values.address
      };
      if (editingUser) {
        const {
          error
        } = await supabase.from("users").update({
          email: values.email,
          role: values.role,
          profile: profile,
          active: values.active,
          updated_at: new Date().toISOString()
        }).eq("id", editingUser.id);
        if (error) throw error;
        toast({
          title: "User Updated",
          description: `${values.email} has been updated.`
        });
      } else {
        const {
          error
        } = await supabase.from("users").insert({
          email: values.email,
          role: values.role,
          profile: profile,
          active: values.active
        });
        if (error) throw error;
        toast({
          title: "User Created",
          description: `${values.email} has been created.`
        });
      }
      setOpenDialog(false);
      form.reset();
      setEditingUser(null);
      fetchUsers();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };
  const handleEdit = (user: any) => {
    setEditingUser(user);
    form.reset({
      email: user.email,
      role: user.role,
      firstName: user.profile?.firstName || "",
      lastName: user.profile?.lastName || "",
      phone: user.profile?.phone || "",
      address: user.profile?.address || "",
      active: user.active
    });
    setOpenDialog(true);
  };
  const handleDelete = async (user: any) => {
    try {
      const {
        error
      } = await supabase.from("users").delete().eq("id", user.id);
      if (error) throw error;
      fetchUsers();
      return true;
    } catch (error: any) {
      toast({
        title: "Error deleting user",
        description: error.message,
        variant: "destructive"
      });
      return false;
    }
  };
  const handleAddNew = () => {
    setEditingUser(null);
    form.reset({
      email: "",
      role: "customer",
      firstName: "",
      lastName: "",
      phone: "",
      address: "",
      active: true
    });
    setOpenDialog(true);
  };
  const filteredUsers = users.filter(user => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();

    // Search in email
    if (user.email.toLowerCase().includes(searchLower)) return true;

    // Search in name
    const firstName = user.profile?.firstName || '';
    const lastName = user.profile?.lastName || '';
    const fullName = `${firstName} ${lastName}`.toLowerCase();
    if (fullName.includes(searchLower)) return true;

    // Search in role
    if (user.role.toLowerCase().includes(searchLower)) return true;
    return false;
  });
  const getRoleIcon = (role: string) => {
    switch (role) {
      case "admin":
      case "manager":
      case "factory_manager":
        return <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-200">
          {role.replace('_', ' ')}
        </Badge>;
      case "customer":
        return <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200">
          Customer
        </Badge>;
      case "tailor":
        return <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
          Tailor
        </Badge>;
      default:
        return <Badge className="capitalize">
          {role.replace('_', ' ')}
        </Badge>;
    }
  };
  const userColumns = [{
    key: "email",
    label: "Email"
  }, {
    key: "name",
    label: "Name",
    render: (row: any) => {
      const firstName = row.profile?.firstName || '';
      const lastName = row.profile?.lastName || '';
      return firstName || lastName ? `${firstName} ${lastName}`.trim() : "N/A";
    }
  }, {
    key: "role",
    label: "Role",
    render: (row: any) => getRoleIcon(row.role)
  }, {
    key: "phone",
    label: "Phone",
    render: (row: any) => row.profile?.phone || "N/A"
  }, {
    key: "active",
    label: "Status",
    render: (row: any) => <Badge className={row.active ? "bg-green-100 text-green-800 border-green-200" : "bg-red-100 text-red-800 border-red-200"}>
          {row.active ? "Active" : "Inactive"}
        </Badge>
  }];
  return <div className="space-y-8">
      <Card className="glass-card p-6 animate-fadeIn">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-2xl font-semibold">Customer Management</h2>
            <p className="text-muted-foreground">
              Manage customer profiles, tailor accounts and users
            </p>
          </div>
          <div className="flex items-center gap-2">
            <RemixIcon name="group-line" className="h-10 w-10 text-primary" size="2x" />
          </div>
        </div>
      </Card>

      <div className="flex gap-4 items-center">
        <div className="relative flex-1">
          <RemixIcon name="search-line" className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size="md" />
          <Input placeholder="Search by name, email or role..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" />
        </div>
        <Button onClick={handleAddNew} className="whitespace-nowrap">
          <RemixIcon name="user-add-line" className="mr-2" />
          Add User
        </Button>
      </div>

      <Card className="p-6">
        <DataTable data={filteredUsers} columns={userColumns} onAdd={handleAddNew} onEdit={handleEdit} onDelete={handleDelete} title="Users" itemName="User" isLoading={loading} />
      </Card>

      <FormDialog title={editingUser ? "Edit User" : "Add New User"} open={openDialog} onOpenChange={setOpenDialog}>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pt-4">
            <FormField control={form.control} name="email" render={({
            field
          }) => <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <RemixIcon name="user-line" className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size="md" />
                      <Input {...field} type="email" placeholder="user@example.com" className="pl-10" />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={form.control} name="role" render={({
            field
          }) => <FormItem>
                  <FormLabel>Role</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="customer">Customer</SelectItem>
                      <SelectItem value="tailor">Tailor</SelectItem>
                      <SelectItem value="factory_manager">Factory Manager</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>} />

            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="firstName" render={({
              field
            }) => <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="First name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />

              <FormField control={form.control} name="lastName" render={({
              field
            }) => <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Last name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>} />
            </div>

            <FormField control={form.control} name="phone" render={({
            field
          }) => <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <RemixIcon name="phone-line" className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size="md" />
                      <Input {...field} placeholder="Phone number" className="pl-10" />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={form.control} name="address" render={({
            field
          }) => <FormItem>
                  <FormLabel>Address</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <RemixIcon name="map-pin-line" className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size="md" />
                      <Input {...field} placeholder="Full address" className="pl-10" />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setOpenDialog(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {editingUser ? "Update User" : "Add User"}
              </Button>
            </div>
          </form>
        </Form>
      </FormDialog>
    </div>;
};

export default Customers;
